import React from 'react';

import { Backdrop, Box, IconButton, Typography } from '@mui/material';
import _ from 'lodash';

import { ReactComponent as ChevronDownIcon } from '../../../../common/assets/icons/chevron_down_icon.svg';
import { ReactComponent as EditIcon } from '../../../../common/assets/icons/edit_icon.svg';
import { User } from '../../../../common/types/User';
import { displayDateString } from '../../../../common/utils/DateUtils';
import AddUserToOrganizationPopup from '../../UsersScreen/components/AddUserToOrganizationPopup';

import EditScanCountPopup from './EditScanCountPopup';
import OverwriteSubscriptionPopup from './OverwriteSubscriptionPopup';

import '../../../../common/styles/main.scss';

type RowProps = {
  title: string;
  text: string;
};

const RowItem: React.FC<RowProps> = ({ title, text, children }) => (
  <Box
    className='row'
    sx={{
      justifyContent: 'space-between',
      marginBottom: '16px',
      width: '100%',
    }}
  >
    <Typography fontFamily={'SFProDisplay-Regular'} color='text.secondary'>
      {title}
    </Typography>
    <Typography
      fontFamily={'SFProDisplay-Regular'}
      sx={{ marginLeft: '16px', textAlign: 'right' }}
    >
      {text}
    </Typography>
    {children}
  </Box>
);

type Props = {
  user: User;
};

const UserInfoContainer: React.FC<Props> = ({ user }) => {
  const [editScanCountOpen, setEditScanCountOpen] = React.useState(false);
  const [overwriteSubscriptionOpen, setOverwriteSubscriptionOpen] =
    React.useState(false);
  const [changeOrganizationOpen, setChangeOrganizationOpen] =
    React.useState(false);

  return (
    <Box
      className='user-content-container'
      style={{
        minWidth: '160px',
        marginTop: '20px',
      }}
    >
      <Backdrop
        open={
          editScanCountOpen ||
          overwriteSubscriptionOpen ||
          changeOrganizationOpen
        }
        sx={{
          color: '#fff',
          zIndex: 99,
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {editScanCountOpen && (
          <EditScanCountPopup
            user={user}
            onClose={() => setEditScanCountOpen(false)}
          />
        )}
        {overwriteSubscriptionOpen && (
          <OverwriteSubscriptionPopup
            user={user}
            onClose={() => setOverwriteSubscriptionOpen(false)}
          />
        )}
        {changeOrganizationOpen && (
          <AddUserToOrganizationPopup
            onClose={() => setChangeOrganizationOpen(false)}
            userToAdd={user}
          />
        )}
      </Backdrop>

      <Box sx={{ marginBottom: '16px' }} className='row'>
        <Typography variant='subtitle'>User Info</Typography>
      </Box>
      <RowItem
        title='Registration date:'
        text={displayDateString(user.createdAt, false)}
      />
      <RowItem
        title='Last login date:'
        text={displayDateString(user.lastLogin, false)}
      />
      <Box
        className='row'
        sx={{
          justifyContent: 'space-between',
          marginBottom: '16px',
        }}
      >
        <Typography fontFamily={'SFProDisplay-Regular'} color='text.secondary'>
          User status:
        </Typography>
        <Typography
          sx={{
            marginLeft: '16px',
            color: (theme) =>
              user.active
                ? theme.palette.appGreen?.main
                : theme.palette.error?.main,
          }}
        >
          {user.active ? 'Active' : 'Inactive'}
        </Typography>
      </Box>
      <Box
        className='row'
        sx={{
          justifyContent: 'space-between',
          marginBottom: '16px',
        }}
      >
        <Typography fontFamily={'SFProDisplay-Regular'} color='text.secondary'>
          Subscription:
        </Typography>
        <Typography
          sx={{
            marginLeft: '16px',
            color: (theme) =>
              user.lastKnownSubscription
                ? theme.palette.appPurple?.main
                : theme.palette.error?.main,
          }}
        >
          {!user.lastKnownSubscription && 'No Subscription'}
          {user.lastKnownSubscription &&
            _.capitalize(user.lastKnownSubscription)}
        </Typography>
      </Box>
      <Box
        className='row'
        sx={{
          justifyContent: 'space-between',
          marginBottom: '16px',
        }}
      >
        <Typography fontFamily={'SFProDisplay-Regular'} color='text.secondary'>
          Overwrite <br /> Subscription:
        </Typography>
        <Typography
          sx={{
            width: '100%',
            textAlign: 'right',
            marginLeft: '12px',
            marginRight: '4px',
            color: (theme) =>
              user.overwriteSubscription
                ? theme.palette.error?.main
                : theme.palette.appGray?.main,
          }}
        >
          {!user.overwriteSubscription && 'None'}
          {user.overwriteSubscription &&
            _.capitalize(user.overwriteSubscription)}
        </Typography>
        <IconButton
          disabled={user.organizationId !== null}
          onClick={() => setOverwriteSubscriptionOpen(true)}
        >
          <EditIcon fill={user.organizationId ? '#ccc' : '#227bef'} />
        </IconButton>
      </Box>
      <Box
        className='row'
        sx={{
          justifyContent: 'space-between',
          marginBottom: '16px',
        }}
      >
        <Typography fontFamily={'SFProDisplay-Regular'} color='text.secondary'>
          Organization:
        </Typography>
        <Typography
          sx={{
            width: '100%',
            textAlign: 'right',
            marginLeft: '12px',
            marginRight: '4px',
            color: (theme) => theme.palette.appGray.main,
          }}
        >
          {!user.organizationName && 'None'}
          {user.organizationName && user.organizationName}
        </Typography>
        <IconButton onClick={() => setChangeOrganizationOpen(true)}>
          <ChevronDownIcon />
        </IconButton>
      </Box>

      <Box
        className='row'
        sx={{
          justifyContent: 'space-between',
          marginBottom: '16px',
        }}
      >
        <Typography
          fontFamily={'SFProDisplay-Regular'}
          color='text.secondary'
          sx={{ width: '100%' }}
        >
          Available scans:
        </Typography>
        <Typography
          sx={{
            width: '100%',
            textAlign: 'right',
            marginLeft: '0px',
            marginRight: '0px',
            color: (theme) => theme.palette.appGray.main,
          }}
        >
          {user.purchasedScansAvailable === -1
            ? 'unlimited'
            : `${user.purchasedScansAvailable}`}
        </Typography>
        <IconButton
          disabled={user.organizationId !== null}
          onClick={() => setEditScanCountOpen(true)}
        >
          <EditIcon fill={user.organizationId ? '#ccc' : '#227bef'} />
        </IconButton>
      </Box>

      <RowItem
        title='Start date:'
        text={
          user.subscriptionPurchaseDate
            ? displayDateString(user.subscriptionPurchaseDate, false)
            : '-'
        }
      />
      <RowItem
        title='Next issue date:'
        text={
          user.subscriptionExpirationDate
            ? displayDateString(user.subscriptionExpirationDate, false)
            : '-'
        }
      />
    </Box>
  );
};

export default UserInfoContainer;
